// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.2
// updated  : 2026-06-19
// protected: NO  -> view-as-role selector (OWNER, read-only) + banner; path absolut.
// =============================================================================
// Shell V2: sidebar role-based + topbar + area konten (Outlet).
import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Menu, LogOut, Eye, X, UserCog, AlertTriangle, ChevronDown, ChevronRight, Sun, Moon, Monitor } from "lucide-react";
import { useV2Auth, ASSIST_OPTIONS } from "@/v2/V2AuthContext";
import { BrandLogo, ClubBadge } from "@/v2/brand";
import { useV2Realtime } from "@/v2/V2RealtimeContext";
import { useTheme } from "@/v2/theme";

// SLICE C1: toggle tema (Terang -> Gelap -> Ikut Sistem). Ikon matahari/bulan/monitor.
const THEME_LABEL = { light: "Terang", dark: "Gelap", system: "Ikut Sistem" };
const THEME_NEXT = { light: "dark", dark: "system", system: "light" };
const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const Icon = theme === "dark" ? Moon : theme === "system" ? Monitor : Sun;
  return (
    <Button
      variant="outline" size="icon" className="h-9 w-9"
      onClick={() => setTheme(THEME_NEXT[theme])}
      aria-label={`Tema: ${THEME_LABEL[theme]} — klik untuk ${THEME_LABEL[THEME_NEXT[theme]]}`}
      title={`Tema: ${THEME_LABEL[theme]} (klik untuk ${THEME_LABEL[THEME_NEXT[theme]]})`}
      data-testid="v2-theme-toggle" data-theme={theme}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );
};

// FASE 7B: indikator koneksi realtime yang halus (tak mengganggu alur kerja).
const LiveDot = () => {
  const { status } = useV2Realtime();
  if (status === "off") return null;  // flag OFF -> tak tampil
  const map = {
    live: { c: "bg-[hsl(var(--badge-success))]", t: "Realtime aktif — data ter-update otomatis" },
    connecting: { c: "bg-[hsl(var(--badge-warning))] animate-pulse", t: "Menyambungkan realtime…" },
    offline: { c: "bg-muted-foreground/50", t: "Realtime terputus — data tetap tampil (buka ulang halaman untuk refresh)" },
  };
  const s = map[status] || map.offline;
  return (
    <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground mr-1"
          title={s.t} data-testid="v2-realtime-status" data-status={status}>
      <span className={`w-2 h-2 rounded-full ${s.c}`} />
      {status === "live" ? "Live" : status === "connecting" ? "…" : "Offline"}
    </span>
  );
};
import { menuForRole, SIDEBAR_GROUPS } from "@/v2/menu";
import { KimiProvider } from "@/v2/components/KimiProvider";
import { KimiWidget, KimiHeaderTrigger } from "@/v2/components/KimiWidget";

// Simpan buka/tutup kelompok PER PENGGUNA di localStorage (dibungkus try/catch).
const GROUP_LS = "etams_v2_sidebar_groups:";
const readGroups = (u) => {
  try { const r = localStorage.getItem(GROUP_LS + u); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
};
const writeGroups = (u, s) => {
  try { localStorage.setItem(GROUP_LS + u, JSON.stringify(s)); }
  catch { /* localStorage gagal -> abaikan, sidebar tetap normal */ }
};

const SideContent = ({ role, onNav, isSuperOwner, openMap = {}, onToggleGroup }) => {
  // D-026: menu "Reset Data Simulasi" hanya untuk Super Owner. FILTER ROLE TIDAK BERUBAH.
  const items = menuForRole(role).filter((m) => m.key !== "uat-reset" || isSuperOwner);
  const byKey = React.useMemo(() => Object.fromEntries(items.map((m) => [m.key, m])), [items]);

  const dashboard = byKey.dashboard;  // SENGAJA di luar kelompok
  const groups = SIDEBAR_GROUPS
    .map((g) => ({ ...g, items: g.keys.map((k) => byKey[k]).filter(Boolean) }))
    .filter((g) => g.items.length > 0);  // kelompok kosong TIDAK ditampilkan

  // SLICE E-1: state buka/tutup DI-ANGKAT ke V2Shell (satu sumber dipakai bersama oleh
  // sidebar desktop & Sheet mobile). Di-key per NAMA GRUP (string) — TIDAK PERNAH per indeks,
  // sehingga filter menu per-role tak menggeser target toggle. Beberapa grup boleh terbuka.
  const isOpen = (id) => (openMap[id] === undefined ? true : !!openMap[id]);
  const toggle = (id) => onToggleGroup?.(id);

  const renderLink = (m) => (
    <NavLink
      key={m.key}
      to={`/${m.path}`}
      end
      onClick={onNav}
      data-testid={`v2-nav-${m.key}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ` +
        (isActive ? "bg-primary-tint text-primary-soft font-medium shadow-[inset_3px_0_0_0_hsl(var(--accent))]"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground")
      }
    >
      <m.icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{m.label}</span>
      {!m.ready && <Badge variant="outline" className="ml-auto text-[10px] py-0">soon</Badge>}
    </NavLink>
  );

  // Jaring pengaman: bila kelak ada menu yg tak masuk kelompok mana pun, JANGAN hilang.
  const grouped = new Set(SIDEBAR_GROUPS.flatMap((g) => g.keys).concat("dashboard"));
  const leftovers = items.filter((m) => !grouped.has(m.key));

  return (
    <nav className="flex flex-col gap-1 p-3" data-testid="v2-sidebar">
      {dashboard && renderLink(dashboard)}
      {groups.map((g) => {
        // SLICE C1 E-1: auto-open hanya sbg NILAI AWAL (default terbuka); pilihan user menang.
        const open = isOpen(g.id);
        return (
          <div key={g.id} className="mt-1">
            <button
              type="button"
              onClick={() => toggle(g.id)}
              aria-expanded={open}
              data-testid={`v2-navgroup-${g.id}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground hover:text-foreground transition-colors"
            >
              {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate">{g.label}</span>
            </button>
            {open && <div className="flex flex-col gap-1 mt-0.5">{g.items.map(renderLink)}</div>}
          </div>
        );
      })}
      {leftovers.length > 0 && <div className="flex flex-col gap-1 mt-1">{leftovers.map(renderLink)}</div>}
    </nav>
  );
};

export const V2Shell = () => {
  const { me, logout, viewAs, setViewAs, assist, setAssist, uatMode } = useV2Auth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = me?.role;
  const isOwner = me?.real_role === "OWNER";
  const isSuperOwner = !!me?.is_super_owner;
  const impersonating = !!me?.impersonating;
  const assistActive = !!me?.assist;
  const assistLabel = ASSIST_OPTIONS.find((o) => o.value === me?.assist_role_full)?.label || me?.assist_role_full;

  // SLICE E-1: SATU sumber state buka/tutup grup untuk sidebar desktop & Sheet mobile.
  // Di-key per NAMA GRUP (string). Nilai awal dari localStorage (default: terbuka).
  const userKey = me?.email || role || "anon";
  const [openGroups, setOpenGroups] = useState(() => readGroups(userKey));
  const toggleGroup = (id) => setOpenGroups((prev) => {
    const cur = prev[id] === undefined ? true : !!prev[id];
    const next = { ...prev, [id]: !cur };
    writeGroups(userKey, next);
    return next;
  });

  const onViewAsChange = (val) => {
    setViewAs(val === "__self__" ? "" : val);
    navigate("/dashboard");  // reset agar tidak terjebak di halaman yg role itu tak punya
  };

  const onAssistChange = (val) => {
    setAssist(val === "__self__" ? "" : val);
    navigate("/dashboard");  // reset agar menu mengikuti role efektif
  };
  const exitAssist = () => onAssistChange("__self__");

  return (
    <KimiProvider>
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:flex-col w-64 border-r bg-card shrink-0">
        <div className="h-16 flex items-center gap-2 px-5 border-b">
          <BrandLogo variant="auto" className="h-8 w-auto" />
        </div>
        <div className="flex-1 overflow-y-auto"><SideContent role={role} isSuperOwner={isSuperOwner} openMap={openGroups} onToggleGroup={toggleGroup} /></div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b flex items-center justify-between px-4 md:px-6 bg-card/60 backdrop-blur sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="outline" size="icon" data-testid="v2-menu-toggle"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 flex flex-col">
                <div className="h-16 flex items-center gap-2 px-5 border-b shrink-0">
                  <BrandLogo variant="auto" className="h-8 w-auto" />
                </div>
                <div className="flex-1 overflow-y-auto">
                  <SideContent role={role} onNav={() => setOpen(false)} openMap={openGroups} onToggleGroup={toggleGroup} />
                </div>
              </SheetContent>
            </Sheet>
            <h1 className="font-semibold text-lg hidden sm:block">Konsol Operasional</h1>
            {/* S-N 2.7: satu-satunya tombol KIMI, di header, sebelah judul ini */}
            <KimiHeaderTrigger />
          </div>
          <div className="flex items-center gap-3">
            {/* Mode Akses (Owner only) — satu selektor: Owner penuh atau bertindak sebagai role lain (Assist, WRITE) */}
            {isOwner && !impersonating && (
              <div className="hidden md:flex items-center gap-1">
                <UserCog className="w-4 h-4 text-muted-foreground" />
                <Select value={me?.assist_role_full || "__self__"} onValueChange={onAssistChange}>
                  <SelectTrigger className="h-9 w-[220px]" data-testid="v2-mode-akses-select">
                    <SelectValue placeholder="Mode Akses..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__self__" data-testid="v2-mode-akses-self">Owner — Akses Penuh</SelectItem>
                    {ASSIST_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`v2-mode-akses-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" data-testid="v2-user-email">{me?.email}</p>
              <p className="text-[11px] text-muted-foreground">
                <Badge variant="secondary" className="mr-1">{role}</Badge>
                <Badge variant="outline">{me?.region || "GLOBAL"}</Badge>
              </p>
            </div>
            <ClubBadge role={role} region={me?.region} size="md" showLabel={false} />
            <LiveDot />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/dashboard"); }} data-testid="v2-logout">
              <LogOut className="w-4 h-4 mr-1" />Keluar
            </Button>
          </div>
        </header>

        {/* D-022A: Banner OWNER ASSIST MODE (WRITE) — selalu tampil di semua halaman saat aktif */}
        {assistActive && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-2.5 bg-destructive/15 border-b-2 border-destructive/50 text-destructive text-sm font-medium"
               data-testid="v2-assist-banner">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <ClubBadge role={role} region={me?.effective_region || me?.region} size="sm" showLabel={false} />
              <span>
                <b>OWNER ASSIST MODE AKTIF</b> — Anda sedang bertindak sebagai <b>{assistLabel}</b>
                {me?.effective_store ? ` (Store ${me.effective_store})` : ` (${me?.effective_region || me?.region})`}.
                {" "}Semua tindakan dicatat atas nama Owner.
              </span>
            </span>
            <Button variant="destructive" size="sm" className="h-7 shrink-0" data-testid="v2-assist-exit"
                    onClick={exitAssist}>
              <X className="w-3.5 h-3.5 mr-1" />Keluar Assist Mode
            </Button>
          </div>
        )}

        {/* Banner mode "lihat sebagai" (read-only) */}
        {impersonating && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-2 bg-[hsl(var(--badge-warning))]/15 border-b border-[hsl(var(--badge-warning))]/40 text-[hsl(var(--badge-warning))] text-sm"
               data-testid="v2-impersonation-banner">
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Mode <b>lihat sebagai {role}</b> ({me?.region}) — <b>read-only</b>. Aksi tulis dinonaktifkan.
            </span>
            <Button variant="outline" size="sm" className="h-7" data-testid="v2-view-as-exit"
                    onClick={() => onViewAsChange("__self__")}>
              <X className="w-3.5 h-3.5 mr-1" />Keluar mode
            </Button>
          </div>
        )}

        <main className="flex-1 p-4 md:p-6 overflow-y-auto"><Outlet /></main>
      </div>
    </div>
    <KimiWidget />
    </KimiProvider>
  );
};
